import { transformObjectToCamelCase } from '@utilities';

import axios from './api';

import { formatInstantSurvey, transformInstantSurvey } from './utilities/helpers';

/**
 * create instant survey
 * @param {Object} options
 * @param {Object} options.instantSurvey
 * @param {AbortSignal} [options.signal]
 * @return {Promise}
 */
export const createInstantSurvey = async ({
  instantSurvey,
  isMultiLevelDisqualifyEnabled = true,
  signal,
}) => {
  return axios({
    data: formatInstantSurvey(instantSurvey),
    method: 'post',
    signal,
    url: '/instantsurvey/instantsurvey',
  }).then(({ data }) => transformInstantSurvey(data, { isMultiLevelDisqualifyEnabled }));
};

/**
 * Creates a modal survey.
 * @async
 * @function createModalSurvey
 * @param {Object} options
 * @param {string} options.country
 * @param {string} options.instantSurveyType
 * @param {Array} options.promptAnswers
 * @param {string} options.title
 * @returns {Promise<Object>} The modal survey response.
 */
export const createModalSurvey = async ({ country, instantSurveyType, promptAnswers, title }) => {
  return axios({
    data: {
      country,
      instant_survey_type: instantSurveyType,
      prompt_answers: promptAnswers,
      title,
    },
    method: 'post',
    url: '/instantsurvey/instantsurvey/create_via_modal',
  }).then(({ data }) => data);
};

/**
 * copy instant survey
 * @param {Object} options
 * @param {Number} options.id
 * @param {AbortSignal} [options.signal]
 * @return {Promise}
 */
export const copyInstantSurvey = async ({ id }) => {
  return axios({
    method: 'post',
    url: `/instantsurvey/instantsurvey/${id}/copy`,
  }).then(({ data }) => data);
};

/**
 * delete instant survey
 * @param {Object} options
 * @param {Number} options.id
 * @return {Promise}
 */
export const deleteInstantSurvey = async ({ id }) => {
  return axios({
    data: {
      deleted: new Date().toISOString(),
    },
    method: 'patch',
    url: `/instantsurvey/instantsurvey/${id}`,
  });
};

/**
 * Force complete an instant survey.
 * @param {Object} options
 * @param {Number} options.atlasSurveyRef
 * @returns {Promise}
 */
export const forceCompleteInstantSurvey = async ({ atlasSurveyRef }) => {
  return axios({
    method: 'post',
    url: `/instantsurvey/instantsurvey/${atlasSurveyRef}/force_close`,
  });
};

/**
 * patch instant survey
 * @param {Object} options
 * @param {Object} options.data
 * @param {Number} options.id
 * @return {Promise}
 */
export const patchInstantSurvey = async ({ data, id }) => {
  return axios({
    data,
    method: 'patch',
    url: `/instantsurvey/instantsurvey/${id}`,
  }).then(({ data }) => transformInstantSurvey(data));
};

/**
 * read instant survey
 * @param {Object} options
 * @param {Number} options.id
 * @param {Object} [options.params]
 * @param {AbortSignal} [options.signal]
 * @return {Promise}
 */
export const readInstantSurvey = async ({
  id,
  isMultiLevelDisqualifyEnabled = true,
  params,
  signal,
}) => {
  return axios({
    method: 'get',
    params: {
      ...params,
    },
    signal,
    url: `/instantsurvey/instantsurvey/${id}`,
  }).then(({ data }) => transformInstantSurvey(data, { isMultiLevelDisqualifyEnabled }));
};

/**
 * update instant survey
 * @param {Object} options
 * @param {Object} options.instantSurvey
 * @param {AbortSignal} [options.signal]
 * @return {Promise}
 */

// Piping mentions use the pattern `answer from q<N>` in richText HTML attributes.
// eslint-disable-next-line security/detect-unsafe-regex
const PIPING_MENTION_REGEX = /data-mention-id="answer from q\d+(?:\.\d+)?"/;

export const buildUpdateInstantSurveyRequest = (instantSurvey) => {
  const { id, ...instantSurveyData } = formatInstantSurvey(instantSurvey);

  const questions = instantSurveyData.questions.map((formattedQuestion, index) => {
    const sourceQuestion = instantSurvey.questions[index];
    // TODO: BE breaking on folder, options, and instant_survey_types being sent
    const { folder, instant_survey_types, options, ...question } = formattedQuestion;

    const shouldSendOptions =
      question?.properties?.is_attention_check ||
      sourceQuestion?.answers?.some(
        (a) => typeof a.richText === 'string' && PIPING_MENTION_REGEX.test(a.richText)
      );

    return shouldSendOptions ? { ...question, options } : question;
  });

  return { id, requestData: { ...instantSurveyData, questions } };
};

export const updateInstantSurvey = async ({
  instantSurvey,
  isMultiLevelDisqualifyEnabled = true,
  signal,
}) => {
  const { id, requestData } = buildUpdateInstantSurveyRequest(instantSurvey);

  return axios({
    data: requestData,
    method: 'put',
    signal,
    url: `/instantsurvey/instantsurvey/${id}`,
  }).then(({ data }) => transformInstantSurvey(data, { isMultiLevelDisqualifyEnabled }));
};

/**
 * validate instant survey (sample size)
 * @param {Object} options
 * @param {Number} options.id
 * @param {AbortSignal} options.signal
 * @return {Promise}
 */
export const validateInstantSurvey = async ({ id, signal }) => {
  return axios({
    method: 'post',
    signal,
    url: `/instantsurvey/instantsurvey/${id}/validate`,
  });
};

/**
 * validate instant survey group (sample size)
 * @param {Object} options
 * @param {Number} options.groupId
 * @param {AbortSignal} options.signal
 * @param {Number} options.surveyId
 * @return {Promise}
 */
export const validateInstantSurveyGroup = async ({ groupId, signal, surveyId }) => {
  return axios({
    method: 'post',
    signal,
    url: `/instantsurvey/instantsurvey/${surveyId}/groups/${groupId}/validate`,
  }).then(({ data }) => transformObjectToCamelCase(data));
};

/**
 * validate instant survey prompts
 * @param {Object} options
 * @param {Number} options.id
 * @return {Promise}
 */
export const validateInstantSurveyPrompts = async ({ id }) => {
  return axios({
    method: 'post',
    url: `/instantsurvey/instantsurvey/${id}/validate_prompts`,
  }).then(({ data }) => data);
};
